/**
 * WorkspaceBanner — Feature 075 (plan 013, Workstream B).
 *
 * Thin bar above the Topbar, visible ONLY for SUB_DELEGATE users. Shows
 * "{Owner}'s Workspace"; with more than one workspace it becomes a
 * dropdown that switches the active workspace (cache cleared + socket
 * re-handshake happen via WorkspaceProvider/store) and returns the user
 * to their role dashboard.
 */

import { useNavigate } from 'react-router-dom';
import { Briefcase, Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { useWorkspace } from '../context/WorkspaceProvider';
import { ownerRoleLabel } from '../utils/labels';
import type { SubDelegateOwnerRole } from '../types/subDelegateInvite';
import type { MyWorkspace } from '../api/myWorkspacesApi';

const workspaceLabel = (workspace: MyWorkspace): string => {
  const ownerName = [workspace.ownerFirstName, workspace.ownerLastName]
    .filter(Boolean)
    .join(' ');
  return `${ownerName}'s Workspace`;
};

export function WorkspaceBanner() {
  const { isDelegate, isLoading, workspaces, activeWorkspace, switchWorkspace } =
    useWorkspace();
  const navigate = useNavigate();

  if (!isDelegate) return null;

  const handleSwitch = (ownerId: string) => {
    if (ownerId === activeWorkspace?.ownerId) return;
    switchWorkspace(ownerId);
    navigate('/dashboard');
  };

  return (
    <div
      className="flex h-9 items-center justify-center bg-[#014a3e] px-4 text-white"
      data-testid="workspace-banner"
    >
      {isLoading && (
        <span className="text-xs text-white/80">Loading your workspaces…</span>
      )}

      {!isLoading && workspaces.length === 0 && (
        <span className="text-xs text-white/90" data-testid="no-workspaces">
          No active workspaces — ask the workspace owner for a new invitation.
        </span>
      )}

      {!isLoading && activeWorkspace && workspaces.length === 1 && (
        <span className="flex items-center gap-1.5 text-xs font-medium">
          <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
          {workspaceLabel(activeWorkspace)}
        </span>
      )}

      {!isLoading && activeWorkspace && workspaces.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors hover:bg-white/10"
              aria-label="Switch workspace"
            >
              <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
              {workspaceLabel(activeWorkspace)}
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-64">
            <DropdownMenuLabel className="text-xs text-gray-500">
              Switch workspace
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {workspaces.map((workspace) => {
              const isActive = workspace.ownerId === activeWorkspace.ownerId;
              return (
                <DropdownMenuItem
                  key={workspace.ownerId}
                  onClick={() => handleSwitch(workspace.ownerId)}
                  className="cursor-pointer"
                >
                  <span className="flex w-full items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm">
                        {workspaceLabel(workspace)}
                      </span>
                      <span className="block text-[11px] text-gray-500">
                        {ownerRoleLabel(
                          workspace.ownerRole as SubDelegateOwnerRole,
                        )}
                      </span>
                    </span>
                    {isActive && (
                      <Check
                        className="h-4 w-4 shrink-0 text-[#026857]"
                        data-testid="active-workspace-check"
                      />
                    )}
                  </span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
